import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import Card from '../../../shared/card/Card';
import MapView from '@arcgis/core/views/MapView';
import Map from '@arcgis/core/Map';
import { Property } from '../../../../models/Property';
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Color from '@arcgis/core/Color';
import errorLogger from '../../../../utilities/errorLogger';

interface MapSectionProps {
  property: Property;
  extent: __esri.Extent;
  map: React.MutableRefObject<__esri.WebMap>;
}

export default function MapSection({ property, extent, map }: MapSectionProps) {
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef(new AbortController());
  const view = useRef(new MapView({
    map: new Map({
      basemap: "hybrid"
    }),
    extent: extent
  }));

  // Create a graphics layer to add the highlighted property geometry
  const graphicsLayer = useRef(new GraphicsLayer());

  useEffect(() => {
    // Add graphics layer to the map
    if (view.current) {
      view.current.map.add(graphicsLayer.current);
    }

    // Cleanup on component unmount
    return () => {
      if (view.current) {
        view.current.map.remove(graphicsLayer.current);
      }
    };
  }, []);

  /**
   * Adjusts zoom level for Summary page map. Map view does not update
   * to the extent of polygon due to map div constantly changing height.
   */
  const getZoomLevel = useCallback(() => {
    const area = geometryEngine.geodesicArea(
      property.geometry as __esri.Polygon,
      "square-miles"
    );

    switch (true) {
      case (area < 0.002):
        return 17;
      case (area < 0.016):
        return 16;
      case (area > 0.016 && area < 0.04):
        return 15;
      case (area < 0.09):
        return 15;
      case (area > 0.009 && area < 0.2):
        return 15;
      case (area < 1):
        return 14;
      case (area > 2):
        return 13;
      default:
        return 15;
    };
  }, [property.geometry]);

  useMemo(() => {
    const source_propertiesLayer = map.current.layers
      .find(layer => layer.title === 'Properties') as __esri.FeatureLayer;

    const duplicates: Array<__esri.Layer> = [];
    view.current.map.layers.forEach(layer => {
      if (layer.type === 'feature') {
        const fl = (layer as FeatureLayer);
        if (fl.portalItem && fl.portalItem === source_propertiesLayer.portalItem) {
          duplicates.push(layer);
        }
      }
    });

    if (duplicates.length > 0) view.current.map.removeMany(duplicates);

    abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    if (property.geometry.extent) {
      view.current.extent = property.geometry.extent;
      view.current.zoom = getZoomLevel();
    } else {
      view.current
        .goTo(property.geometry, { signal })
        .catch(errorLogger);
      view.current.zoom = 18;
    }

    // Highlight property geometry with a graphic
    if (property.geometry) {
      const highlightSymbol = new SimpleFillSymbol({
        outline: new SimpleLineSymbol({
          color: new Color([255, 201, 7]),  // lighter orange border
          width: 2
        })
      });

      const graphic = new Graphic({
        geometry: property.geometry,
        symbol: highlightSymbol
      });

      graphicsLayer.current.removeAll();  // Clear any previous highlights
      graphicsLayer.current.add(graphic); // Add the new graphic to the graphics layer
    }
  }, [map, property, getZoomLevel]);

  return (
    <section className='map-section'>
      <Card>
        <div className='location-title'>Location</div>
        <div className="summary-view-container">
          <div
            className="summary-map-container"
            ref={(node) => {
              mapDiv.current = node;
              if (mapDiv.current) {
                view.current.container = mapDiv.current;
              }
            }}
          ></div>
        </div>
      </Card>
    </section>
  );
}
